import type { AppVariant } from '@open-songster/core'
import { chooseAppVariant, isAppVariant } from '@open-songster/core'
import { readString, removeKey, writeString } from './storage'

/**
 * One app, one URL, two front ends. The host decides at startup which one to
 * load — the game for phones, the editor for wide screens — and the user can
 * pin either with `?variant=` or from the settings of the variant they are in.
 */

export const VARIANT_STORAGE_KEY = 'songster.variant'

/** Query parameter that forces a variant, e.g. a link to `/?variant=desktop`. */
export const VARIANT_QUERY_PARAM = 'variant'

export interface VariantEnvironment {
  location: Pick<Location, 'search'>
  navigator: Pick<Navigator, 'userAgent'>
  innerWidth: number
}

/** The variant the user asked for explicitly, if any. */
export function readVariantOverride(): AppVariant | null {
  const stored = readString(VARIANT_STORAGE_KEY)
  return isAppVariant(stored) ? stored : null
}

/** Remembers an explicit choice; `null` goes back to detecting the device. */
export function setVariantOverride(variant: AppVariant | null): void {
  if (variant === null) removeKey(VARIANT_STORAGE_KEY)
  else writeString(VARIANT_STORAGE_KEY, variant)
}

/**
 * Which front end to load for this visit. A `?variant=` parameter wins and is
 * remembered as the user's choice; a remembered choice comes next; otherwise
 * the device decides.
 */
export function resolveAppVariant(environment: Partial<VariantEnvironment> = {}): AppVariant {
  const location = environment.location ?? window.location
  const userAgent = (environment.navigator ?? window.navigator).userAgent
  const viewportWidth = environment.innerWidth ?? window.innerWidth

  const requested = new URLSearchParams(location.search).get(VARIANT_QUERY_PARAM)
  let override = readVariantOverride()
  if (isAppVariant(requested)) {
    override = requested
    setVariantOverride(requested)
  }

  return chooseAppVariant({ userAgent, viewportWidth, override })
}
